import { useCallback, useEffect, useMemo, useState } from "react"
import { ApiError, Box, CenteredLoader, Dropdown } from "@shared/primitive-components"
import { unwrap } from "@shared/utils/helpers/unwrap"
import { TspList, addPolicyAssociations } from "../../../api/policy"
import { CurrentTspsList } from "./CurrentTspsList"
import * as styles from "../../styles"
import { useFindPolicyAssociations, useTSPsList, useAuth } from "../../../contexts"

export const TSPS_SECTION_DTI = {
  ROOT: `tsps_section_root`,
  LIST: `tsps_section_list`,
}

// --------------------------------------------------------------------------------------------------------------------

const ALLOWED_NUMBER_OF_TSPS_PER_POLICY = 5

// --------------------------------------------------------------------------------------------------------------------

export const TspsSection = ({ policyId: id }: { policyId: string }) => {
  const { apiErrored, loading, data } = useFindPolicyAssociations({ id })
  const { apiErrored: apiErr, data: dropdownTSPsList } = useTSPsList()

  const [tspsList, setTspsList] = useState<TspList>(data)

  const { getToken } = useAuth()

  useEffect(() => {
    if (!loading && !apiErrored) {
      setTspsList(data)
    }
  }, [data, loading, apiErrored])

  const filteredDropdownTSPsList = useMemo(() => {
    const dropdownTSPsNames = dropdownTSPsList.map((tsp) => tsp.name)
    const policyTSPsNames = tspsList.map((policyTsp) => policyTsp.name)
    return dropdownTSPsNames.filter((dropdownTSPName) => !policyTSPsNames.includes(dropdownTSPName))
  }, [dropdownTSPsList, tspsList])

  const selectedValueDropdown = useCallback(
    (selectedTspName: string) => {
      const [selectedTsp] = dropdownTSPsList.filter((tsp) => tsp.name === selectedTspName)
      if (!selectedTsp) return
      setTspsList([...tspsList, selectedTsp])
      const newTSPsIdsList = [...tspsList.map((tsp) => tsp.id), selectedTsp.id]
      addPolicyAssociations({ id, tspIds: newTSPsIdsList }, async () => unwrap(await getToken()))
    },
    [dropdownTSPsList, tspsList, setTspsList, id, getToken]
  )

  const removeTsp = useCallback(
    (tspId: string) => {
      const updatedTspsList = tspsList.filter((tsp) => tsp.id !== tspId)
      const tspIds = updatedTspsList.map((tsp) => tsp.id)

      addPolicyAssociations({ id, tspIds }, async () => unwrap(await getToken()))
      setTspsList(updatedTspsList)
    },
    [id, setTspsList, tspsList, getToken]
  )

  if (apiErrored || apiErr) return <ApiError />

  const { Root, Trigger, Content, Item } = Dropdown

  return loading ? (
    <CenteredLoader top="20px" />
  ) : (
    <>
      <CurrentTspsList tspsList={tspsList} onRemove={removeTsp} />
      {tspsList.length < ALLOWED_NUMBER_OF_TSPS_PER_POLICY && (
        <Box css={styles.DropdownContainer}>
          <Root data-testid={TSPS_SECTION_DTI.ROOT} onValueSelect={selectedValueDropdown}>
            <Trigger css={styles.DropdownTrigger}>Add your TSP</Trigger>
            <Content>
              {filteredDropdownTSPsList.map((i) => (
                <Item value={i} key={i}>
                  {i}
                </Item>
              ))}
            </Content>
          </Root>
        </Box>
      )}
    </>
  )
}
